import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const EquityCurve = ({ trades = [], startingBalance = null }) => {
  const chartData = useMemo(() => {
    if (trades.length === 0) return [];

    const sorted = [...trades]
      .filter(t => t.trade_date || t.closeTime)
      .sort((a, b) =>
        new Date(a.trade_date || a.closeTime).getTime() -
        new Date(b.trade_date || b.closeTime).getTime()
      );

    let cumulative = startingBalance ? Number(startingBalance) : 0;
    return sorted.map((trade) => {
      cumulative += Number(trade.profit) || 0;
      const date = new Date(trade.trade_date || trade.closeTime);
      return {
        date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        equity: parseFloat(cumulative.toFixed(2)),
      };
    });
  }, [trades, startingBalance]);

  if (chartData.length === 0) {
    return (
      <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4 md:p-6">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Equity Curve</h3>
        </div>
        <p className="text-sm text-muted-foreground">No trade data available.</p>
      </div>
    );
  }

  const finalEquity = chartData[chartData.length - 1].equity;
  const base = startingBalance ? Number(startingBalance) : 0;
  const pnl = finalEquity - base;
  const isPositive = pnl >= 0;
  const returnPct = base > 0 ? ((pnl / base) * 100).toFixed(2) : null;

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="text-sm font-semibold text-foreground">Equity Curve</h3>
        </div>
        <div className="flex items-center gap-2">
          {startingBalance && (
            <span className={`text-sm font-semibold ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
              ${finalEquity.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          )}
          <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
            isPositive ? 'bg-green-500/10 text-green-500' : 'bg-red-500/10 text-red-500'
          }`}>
            {isPositive ? '+' : ''}{returnPct !== null ? `${returnPct}%` : `$${pnl.toFixed(2)}`}
          </span>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={200}>
        <LineChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            interval="preserveStartEnd"
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            tickFormatter={(v) => `$${v.toLocaleString()}`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '6px',
              color: 'hsl(var(--foreground))',
            }}
            formatter={(value) => [`$${Number(value).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 'Equity']}
          />
          {startingBalance && (
            <ReferenceLine
              y={Number(startingBalance)}
              stroke="hsl(var(--muted-foreground))"
              strokeDasharray="4 4"
              strokeOpacity={0.5}
              label={{
                value: 'Start',
                position: 'left',
                fill: 'hsl(var(--muted-foreground))',
                fontSize: 10,
              }}
            />
          )}
          <Line
            type="monotone"
            dataKey="equity"
            stroke={isPositive ? '#22c55e' : '#ef4444'}
            strokeWidth={2}
            dot={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default EquityCurve;
